import React, { useCallback, useEffect, useState } from 'react';
import PropTypes from 'prop-types';
import Loader from '../../../../component/common/Loader';
import SearchInput from '../../../../component/common/Input/SearchInput';
import Table from '../../../../component/common/Table';
import Pagination from '../../../../component/common/table2/Pagination';
import { minsToHhMm } from '../../../SleepHub/sleepHubApi';
import { fetchCompanyPodsUsers } from './companyPodsApi';
import { TableCard } from '../../../../component/common/designSystem';

const PER_PAGE = 10;

/**
 * Searchable users table for the Shoorah Pods section (columns are play-shaped,
 * so this is separate from the Sleep and Breathwork tables).
 *
 * Every user of the population is listed, including those who have never
 * played a pod — they show dashes rather than zeros, so "hasn't started" reads
 * differently from "listened, then stopped".
 */
const PodsUsersTable = ({
  fetcher = fetchCompanyPodsUsers,
  onOpenUser,
  tableName,
  showCompanyColumn = false,
  department = ''
}) => {
  const [loader, setLoader] = useState(false);
  const [rows, setRows] = useState([]);
  const [totalCount, setTotalCount] = useState(0);
  const [currentPage, setCurrentPage] = useState(1);
  const [searchTerm, setSearchTerm] = useState('');

  const fetchData = useCallback(
    async (page, search) => {
      setLoader(true);
      try {
        const response = await fetcher({ page, perPage: PER_PAGE, search, department });
        const mapped = (response.data || []).map((row) => ({
          ...row,
          playsText: row.totalPlays > 0 ? row.totalPlays.toLocaleString() : '-',
          podsPlayedText: row.totalPlays > 0 ? `${row.podsPlayed ?? 0}` : '-',
          listenTimeText: row.totalPlays > 0 ? minsToHhMm(row.totalListenTimeMins) : '-',
          lastPlayDate: row.lastPlayDate || '-'
        }));
        setRows(mapped);
        setTotalCount(response.totalCount || 0);
      } catch (error) {
        console.error('PodsUsersTable fetchData error:', error);
        setRows([]);
        setTotalCount(0);
      } finally {
        setLoader(false);
      }
    },
    [fetcher, department]
  );

  // Debounced fetch on search / page / scope change.
  useEffect(() => {
    const timeout = setTimeout(() => {
      fetchData(currentPage, searchTerm);
    }, 200);
    return () => clearTimeout(timeout);
  }, [fetchData, searchTerm, currentPage]);

  const columns = [
    { title: 'Name', key: 'user_profile', sortable: false, align: 'left', type: 'profile' },
    ...(showCompanyColumn
      ? [{ title: 'Company', key: 'company', sortable: false, align: 'center', type: 'text3' }]
      : []),
    { title: 'Plays', key: 'playsText', sortable: false, align: 'center', type: 'text3' },
    { title: 'Pods', key: 'podsPlayedText', sortable: false, align: 'center', type: 'text3' },
    {
      title: 'Listening Time',
      key: 'listenTimeText',
      sortable: false,
      align: 'center',
      type: 'text3'
    },
    { title: 'Last Played', key: 'lastPlayDate', sortable: false, align: 'center', type: 'text3' },
    { title: '', key: 'action', align: 'right', type: 'action', arrowClickFun: onOpenUser }
  ];

  return (
    <div>
      {loader ? <Loader /> : null}

      <div className="flex justify-end">
        <SearchInput
          id={`${tableName}-search`}
          name={`${tableName}-search`}
          type="text"
          value={searchTerm}
          onChange={(e) => {
            setCurrentPage(1);
            setSearchTerm(e.target.value);
          }}
          placeholder="Search users"
          className="w-full sm:w-auto px-3 py-2"
        />
      </div>

      <TableCard
          pagination={{
            currentPage: currentPage,
            totalCount: totalCount,
            pageSize: PER_PAGE,
            onPageChange: (page) => setCurrentPage(page)
          }}
        >
          <Table
          columns={columns}
          data={rows}
          name={tableName}
          bottomBorder={totalCount > PER_PAGE}
          loader={loader}
        />
        </TableCard>

      
    </div>
  );
};

PodsUsersTable.propTypes = {
  fetcher: PropTypes.func,
  onOpenUser: PropTypes.func,
  tableName: PropTypes.string,
  showCompanyColumn: PropTypes.bool,
  department: PropTypes.string
};

export default PodsUsersTable;
